import { Button, Icons, StateScreen } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  customerBackendApi,
  type IncomingContactRequest,
  type SentContactRequest,
} from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import type { InboxItem } from "../features/inbox/inbox.ts";
import { type SocialRequest, useSession } from "../features/session/session-context.tsx";

export const Route = createFileRoute("/requests")({
  component: RequestsScreen,
});

function RequestsScreen() {
  const { backendMode } = useSession();
  return backendMode ? <BackendRequests /> : <MockRequests />;
}

/** Backend-mode contact inbox: live GET /contact-requests + accept/reject/block. */
function BackendRequests() {
  const t = useT();
  const navigate = useNavigate();
  const { respondContact, toast } = useSession();
  const requestsQuery = useQuery({
    queryKey: ["customer", "contact-requests"],
    queryFn: () =>
      customerBackendApi?.listContactRequests() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    refetchInterval: 8_000,
  });
  const incoming = (requestsQuery.data?.incoming ?? []).filter((r) => r.state !== "rejected");
  const sent = requestsQuery.data?.sent ?? [];
  const hasRequests = incoming.length > 0 || sent.length > 0;

  const respond = async (id: string, action: "accept" | "reject" | "block", okText: string) => {
    try {
      await respondContact(id, action);
      toast(okText);
      await requestsQuery.refetch();
    } catch {
      // respondContact already surfaced a toast on failure.
    }
  };

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/me" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="space-y-1">
        <div className="jod-kicker">{t("requests.kicker")}</div>
        <h1 className="h-display text-[1.6rem]">Contact requests</h1>
        <p className="text-cream-3 text-sm">{t("requests.subtitle")}</p>
      </div>

      {!hasRequests ? (
        <div className="h-[70vh]">
          <StateScreen
            title={requestsQuery.isLoading ? t("requests.loading") : t("requests.emptyTitle")}
            body={t("requests.emptyBody")}
            action={t("requests.viewInbox")}
            onAction={() => navigate({ to: "/gifts/incoming" })}
          />
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          <section className="space-y-3">
            <div className="jod-kicker">{t("requests.awaitingYou")}</div>
            {incoming.length === 0 ? (
              <p className="text-cream-3 text-sm">{t("requests.noIncoming")}</p>
            ) : null}
            {incoming.map((req) => (
              <BackendIncomingCard
                key={req.id}
                request={req}
                onAccept={() => respond(req.id, "accept", t("requests.acceptToastConsent"))}
                onReject={() => respond(req.id, "reject", t("requests.rejectToast"))}
                onBlock={() => respond(req.id, "block", t("inbox.blockToast"))}
              />
            ))}
          </section>

          <section className="space-y-3">
            <div className="jod-kicker">{t("requests.sentSection")}</div>
            {sent.length === 0 ? (
              <p className="text-cream-3 text-sm">{t("requests.noSent")}</p>
            ) : null}
            {sent.map((req) => (
              <BackendSentCard key={req.id} request={req} />
            ))}
          </section>
        </div>
      )}
    </div>
  );
}

function BackendIncomingCard({
  request,
  onAccept,
  onReject,
  onBlock,
}: {
  request: IncomingContactRequest;
  onAccept: () => void;
  onReject: () => void;
  onBlock: () => void;
}) {
  const t = useT();
  const accepted = request.state === "accepted";
  const label =
    request.requesterTableCode ?? request.requesterTableLabel ?? t("inbox.unknownTable");
  return (
    <div className="glass-hi rounded-xl p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
          <Icons.IconIG size={21} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-cream">
            {t("inbox.greetsYou", { table: label })}
          </div>
          <p className="mt-2 text-cream-3 text-xs">{t("requests.consentNote")}</p>
        </div>
      </div>
      {accepted ? (
        <div className="mt-3 flex items-center gap-2 text-lime text-sm">
          <Icons.IconCheck size={16} /> {t("requests.acceptedConsent")}
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="accent" className="flex-1" onClick={onAccept}>
            {t("inbox.acceptContact")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onReject}>
            {t("requests.reject")}
          </Button>
          <Button size="sm" variant="destructive" onClick={onBlock}>
            <Icons.IconShield size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}

function BackendSentCard({ request }: { request: SentContactRequest }) {
  const t = useT();
  const shared = request.state === "accepted" ? (request.sharedContact ?? []) : [];
  return (
    <div className="glass rounded-lg px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-cream text-sm">{t("requests.kicker")}</div>
          <div className="text-cream-3 text-xs">
            {request.messagePresetKey ?? t("requests.presetFallback")}
          </div>
        </div>
        <span className="rounded-full border border-hairline px-2.5 py-1 text-[0.65rem] text-gold">
          {request.state}
        </span>
      </div>
      {shared.length > 0 ? (
        <div className="mt-2 space-y-1">
          {shared.map((contact) => (
            <div
              key={`${contact.provider}-${contact.handle}`}
              className="flex items-center gap-2 text-lime text-sm"
            >
              <Icons.IconCheck size={15} /> {contact.provider}: {contact.handle}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Mock-mode contact inbox: local store. */
function MockRequests() {
  const t = useT();
  const navigate = useNavigate();
  const { inbox, sentSocial, respondInbox, toast } = useSession();
  const incoming = inbox.filter((item) => item.kind === "contact" && item.state !== "declined");
  const outgoing = sentSocial.filter((request) => request.kind === "contact");
  const hasRequests = incoming.length > 0 || outgoing.length > 0;

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/me" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="space-y-1">
        <div className="jod-kicker">{t("requests.kicker")}</div>
        <h1 className="h-display text-[1.6rem]">Contact requests</h1>
        <p className="text-cream-3 text-sm">{t("requests.subtitle")}</p>
      </div>

      {!hasRequests ? (
        <div className="h-[70vh]">
          <StateScreen
            title={t("requests.emptyTitle")}
            body={t("requests.emptyBody")}
            action={t("requests.viewInbox")}
            onAction={() => navigate({ to: "/gifts/incoming" })}
          />
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          <section className="space-y-3">
            <div className="jod-kicker">{t("requests.awaitingYou")}</div>
            {incoming.length === 0 ? (
              <p className="text-cream-3 text-sm">{t("requests.noIncoming")}</p>
            ) : null}
            {incoming.map((item) => (
              <IncomingRequestCard
                key={item.id}
                item={item}
                onAccept={() => {
                  respondInbox(item.id, "accept");
                  toast(t("requests.acceptFromToast", { table: item.fromTable }));
                }}
                onDecline={() => respondInbox(item.id, "decline")}
                onBlock={() => {
                  respondInbox(item.id, "block");
                  toast(t("requests.blockTableToast", { table: item.fromTable }));
                }}
              />
            ))}
          </section>

          <section className="space-y-3">
            <div className="jod-kicker">{t("requests.sentSection")}</div>
            {outgoing.length === 0 ? (
              <p className="text-cream-3 text-sm">{t("requests.noSent")}</p>
            ) : null}
            {outgoing.map((request) => (
              <OutgoingRequestCard key={request.id} request={request} />
            ))}
          </section>
        </div>
      )}
    </div>
  );
}

function IncomingRequestCard({
  item,
  onAccept,
  onDecline,
  onBlock,
}: {
  item: InboxItem;
  onAccept: () => void;
  onDecline: () => void;
  onBlock: () => void;
}) {
  const t = useT();
  const accepted = item.state === "accepted";
  return (
    <div className="glass-hi rounded-xl p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
          <Icons.IconIG size={21} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-cream">
            {t("requests.asksFor", { table: item.fromTable, subject: item.subject })}
          </div>
          {item.message ? <p className="mt-1 text-cream-3 text-sm">“{item.message}”</p> : null}
          <p className="mt-2 text-cream-3 text-xs">{t("requests.consentNote")}</p>
        </div>
      </div>
      {accepted ? (
        <div className="mt-3 flex items-center gap-2 text-lime text-sm">
          <Icons.IconCheck size={16} /> {t("requests.acceptedConsent")}
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="accent" className="flex-1" onClick={onAccept}>
            {t("inbox.acceptContact")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDecline}>
            {t("requests.reject")}
          </Button>
          <Button size="sm" variant="destructive" onClick={onBlock}>
            <Icons.IconShield size={16} />
          </Button>
        </div>
      )}
    </div>
  );
}

function OutgoingRequestCard({ request }: { request: SocialRequest }) {
  const t = useT();
  return (
    <div className="glass rounded-lg px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-cream text-sm">
            {t("common.toTable", { table: request.toTable })}
          </div>
          <div className="text-cream-3 text-xs">{request.subject}</div>
        </div>
        <span className="rounded-full border border-hairline px-2.5 py-1 text-[0.65rem] text-gold">
          {request.status}
        </span>
      </div>
    </div>
  );
}
